import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTheme, tokens } from "./ThemeContext";
import { getPost } from "./posts/index";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function ThemeToggle({ t }) {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32, borderRadius: 8, cursor: "pointer",
        background: t.toggleBg, border: `1px solid ${t.navBorder}`,
        color: t.toggleColor, fontSize: 15, flexShrink: 0,
      }}
    >
      {theme === "dark" ? "☀️" : "🌙"}
    </button>
  );
}

// Build markdown component overrides from theme tokens
function makeMdComponents(t) {
  return {
    h1: ({ children }) => (
      <h1 style={{ fontSize: 32, fontWeight: 800, color: t.textPrimary, margin: "0 0 24px", lineHeight: 1.2, fontFamily: "'Syne', sans-serif" }}>
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ fontSize: 22, fontWeight: 700, color: t.textPrimary, margin: "48px 0 16px", lineHeight: 1.3, fontFamily: "'Syne', sans-serif", borderBottom: `1px solid ${t.hrColor}`, paddingBottom: 10 }}>
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ fontSize: 17, fontWeight: 700, color: t.textPrimary, margin: "32px 0 12px", lineHeight: 1.4, fontFamily: "'Syne', sans-serif" }}>
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p style={{ fontSize: 15, color: t.textBody, lineHeight: 1.8, margin: "0 0 18px", fontFamily: "'IBM Plex Mono', monospace" }}>
        {children}
      </p>
    ),
    strong: ({ children }) => (
      <strong style={{ color: t.textPrimary, fontWeight: 600 }}>{children}</strong>
    ),
    em: ({ children }) => (
      <em style={{ color: t.textBody, fontStyle: "italic" }}>{children}</em>
    ),
    a: ({ href, children }) => (
      <a href={href} style={{ color: t.accent, textDecoration: "underline", textDecorationColor: `${t.accent}60` }}>
        {children}
      </a>
    ),
    ul: ({ children }) => (
      <ul style={{ margin: "0 0 18px", paddingLeft: 22, color: t.textBody, fontFamily: "'IBM Plex Mono', monospace", fontSize: 15, lineHeight: 1.8 }}>
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol style={{ margin: "0 0 18px", paddingLeft: 22, color: t.textBody, fontFamily: "'IBM Plex Mono', monospace", fontSize: 15, lineHeight: 1.8 }}>
        {children}
      </ol>
    ),
    li: ({ children }) => <li style={{ marginBottom: 6 }}>{children}</li>,
    hr: () => <hr style={{ border: "none", borderTop: `1px solid ${t.hrColor}`, margin: "40px 0" }} />,
    blockquote: ({ children }) => (
      <blockquote style={{ borderLeft: `3px solid ${t.accent}`, paddingLeft: 18, margin: "24px 0", color: t.textSecondary }}>
        {children}
      </blockquote>
    ),
    // ── Tables — fixed with remark-gfm ──────────────────────────────────────
    table: ({ children }) => (
      <div style={{ overflowX: "auto", margin: "24px 0", borderRadius: 10, border: `1px solid ${t.cardBorder}` }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "'IBM Plex Mono', monospace" }}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead style={{ background: t.tableThead }}>{children}</thead>
    ),
    th: ({ children }) => (
      <th style={{ padding: "10px 14px", textAlign: "left", color: t.accent, fontWeight: 700, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: `1px solid ${t.cardBorder}`, whiteSpace: "nowrap" }}>
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td style={{ padding: "9px 14px", color: t.tableRowText, borderBottom: `1px solid ${t.hrColor}` }}>
        {children}
      </td>
    ),
    tr: ({ children }) => (
      <tr
        onMouseEnter={e => e.currentTarget.style.background = t.tableRow}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        style={{ transition: "background 0.1s" }}
      >
        {children}
      </tr>
    ),
    code: ({ children, inline }) =>
      inline ? (
        <code style={{ background: t.codeBg, color: t.accent, padding: "2px 6px", borderRadius: 4, fontSize: 13, fontFamily: "'IBM Plex Mono', monospace" }}>
          {children}
        </code>
      ) : (
        <pre style={{ background: t.codeBg, padding: "16px 20px", borderRadius: 8, overflowX: "auto", margin: "16px 0" }}>
          <code style={{ color: t.textBody, fontSize: 13, fontFamily: "'IBM Plex Mono', monospace" }}>{children}</code>
        </pre>
      ),
  };
}

export default function BlogPost() {
  const { slug } = useParams();
  const { theme } = useTheme();
  const t = tokens[theme];
  const post = getPost(slug);

  React.useEffect(() => {
    if (post) document.title = `${post.title} | WorkplacePrompts.ai`;
    document.body.style.background = t.appBg;
  }, [post, t.appBg]);

  if (!post) return <Navigate to="/blog" replace />;

  const mdComponents = makeMdComponents(t);
  const CATEGORY_COLOR = { "Tipped Wages": "#F59E0B", "FLSA": "#818CF8", "Maryland": "#10B981" }[post.category] || "#F87171";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        body { background: ${t.appBg}; transition: background 0.2s; }
      `}</style>

      <div style={{ minHeight: "100vh", background: t.appBg, fontFamily: "'Syne', sans-serif", transition: "background 0.2s" }}>

        {/* Nav */}
        <div style={{ background: t.navBg, borderBottom: `1px solid ${t.navBorder}`, padding: "0 32px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
          <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, background: "linear-gradient(135deg, #F59E0B, #D97706)", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>⚖️</div>
            <span style={{ fontSize: 14, fontWeight: 800, color: t.textPrimary, letterSpacing: "0.02em" }}>
              WorkplacePrompts<span style={{ color: t.accent }}>.ai</span>
            </span>
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Link to="/blog" style={{ fontSize: 13, color: t.textSecondary, textDecoration: "none", fontFamily: "'IBM Plex Mono', monospace" }}>
              ← All articles
            </Link>
            <ThemeToggle t={t} />
          </div>
        </div>

        {/* Post header */}
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "52px 32px 40px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 20, background: `${CATEGORY_COLOR}14`, border: `1px solid ${CATEGORY_COLOR}30`, color: CATEGORY_COLOR, fontFamily: "'IBM Plex Mono', monospace" }}>
              {post.category}
            </span>
            <span style={{ fontSize: 12, color: t.textSecondary, fontFamily: "'IBM Plex Mono', monospace" }}>
              {formatDate(post.date)} · {post.readTime}
            </span>
          </div>

          <h1 style={{ fontSize: 34, fontWeight: 800, color: t.textPrimary, margin: "0 0 16px", lineHeight: 1.2 }}>
            {post.title}
          </h1>
          <p style={{ fontSize: 16, color: t.textSecondary, margin: 0, lineHeight: 1.7, fontFamily: "'IBM Plex Mono', monospace" }}>
            {post.description}
          </p>

          {/* Tool CTA bar */}
          <div style={{ marginTop: 32, padding: "16px 20px", background: t.accentBg, border: `1px solid ${t.accentBorder}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <span style={{ fontSize: 13, color: t.textBody, fontFamily: "'IBM Plex Mono', monospace" }}>
              Check your state's compliance instantly — free, no login required.
            </span>
            <Link to="/" style={{ padding: "8px 18px", background: t.accent, color: "#0D1321", borderRadius: 8, fontWeight: 800, fontSize: 13, textDecoration: "none", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
              Use the Checker →
            </Link>
          </div>
        </div>

        {/* Post body */}
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 32px 80px" }}>
          <ReactMarkdown components={mdComponents} remarkPlugins={[remarkGfm]}>
            {post.content}
          </ReactMarkdown>
        </div>

        {/* Bottom CTA */}
        <div style={{ borderTop: `1px solid ${t.navBorder}`, padding: "48px 32px", textAlign: "center" }}>
          <p style={{ color: t.textSecondary, fontSize: 14, fontFamily: "'IBM Plex Mono', monospace", marginBottom: 8 }}>
            Need to verify your current payroll setup?
          </p>
          <p style={{ color: t.textMuted, fontSize: 13, fontFamily: "'IBM Plex Mono', monospace", marginBottom: 24 }}>
            The free compliance checker covers all 50 states and takes under 60 seconds.
          </p>
          <Link to="/" style={{ display: "inline-block", padding: "13px 32px", background: t.accent, color: "#0D1321", borderRadius: 10, fontWeight: 800, fontSize: 15, textDecoration: "none", letterSpacing: "0.04em" }}>
            Run a Free Compliance Check →
          </Link>
          <div style={{ marginTop: 48 }}>
            <Link to="/blog" style={{ fontSize: 13, color: t.textSecondary, textDecoration: "none", fontFamily: "'IBM Plex Mono', monospace" }}>
              ← Back to all articles
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
